import { z } from 'zod'

export type AirflowJobConfig = {
  module: string
  job: string
  args?: Record<string, unknown>
  has_side_effects?: boolean
  stack?: 'fasb' | 'sourced' | 'fasb/sourced'
}

export type AirflowDagContext = {
  start_date?: string
  end_date?: string
  catch_up?: boolean
  merge?: boolean
  run_name?: string
  job_config?: AirflowJobConfig[]
}

export enum AirflowTaskInstanceState {
  Success = 'success',
  Running = 'running',
  Failed = 'failed',
  UpstreamFailed = 'upstream_failed',
  Skipped = 'skipped',
  UpForRetry = 'up_for_retry',
  UpForReschedule = 'up_for_reschedule',
  Queued = 'queued',
  None = 'none',
  Scheduled = 'scheduled',
  Deferred = 'deferred',
  Removed = 'removed',
  Restarting = 'restarting',
}

export const airflowTaskInstanceSchema = z.object({
  task_id: z.string(),
  // Added in Airflow 2.9.0 (we're on 2.8.1)
  // task_display_name: z.string()
  dag_id: z.string(),
  dag_run_id: z.string(),
  start_date: z.string().nullable(),
  end_date: z.string().nullable(),
  duration: z.number().nullable(),
  state: z.nativeEnum(AirflowTaskInstanceState).nullable(),
  try_number: z.number(),
  map_index: z.number(),
  queued_when: z.string().nullable(),
  // Added in Airflow 2.9.0 (we're on 2.8.1)
  // rendered_map_index: z.string().nullable()

  // Custom addition to Airflow Response
  container_id: z.string().optional(),
})

export type AirflowTaskInstance = z.infer<typeof airflowTaskInstanceSchema>

export enum AirflowDagRunState {
  Queued = 'queued',
  Running = 'running',
  Success = 'success',
  Failed = 'failed',
}

export const airflowDagRunSchema = z.object({
  dag_run_id: z.string(),
  dag_id: z.string(),
  start_date: z.string().nullable(),
  end_date: z.string().nullable(),
  run_type: z.enum(['backfill', 'manual', 'scheduled', 'dataset_triggered']),
  state: z.nativeEnum(AirflowDagRunState),
  conf: z.record(z.any()),
})

export type AirflowDagRun = z.infer<typeof airflowDagRunSchema>

export const airflowDagRunsResponseSchema = z.object({ dag_runs: z.array(airflowDagRunSchema) })

export type AirflowDagRunsResponse = z.infer<typeof airflowDagRunsResponseSchema>

export const airflowDagNextRunSchema = z.object({ next_dag_run: z.string().nullable() })

export type AirflowDagNextRun = z.infer<typeof airflowDagNextRunSchema>
